/**
 * <dynamic-dates>
 *
 * Powers the "shipping checkpoints" block. Reads config from data-*
 * attributes set by Liquid, computes a start/end date for each
 * checkpoint based on min/max day offsets, skips configured weekdays
 * and specific calendar dates, formats the result, and swaps it into
 * any [start_date] / [end_date] placeholders inside the checkpoint text.
 *
 * Expected data attributes on <dynamic-dates>:
 *   data-date-format   one of the fixed preset values from the block's
 *                       `date_format` select setting:
 *                         day_mm_dd          -> "Monday, February 1st"
 *                         day_dd_mm          -> "Monday, 1. February"
 *                         mm_dd              -> "February 1st"
 *                         dd_mm              -> "1. February"
 *                         dd_mm_no_dot       -> "1 February"
 *                         day_dd_mm_numeric  -> "Monday, 01. 02."
 *                         dd_mm_numeric      -> "01. 02."
 *                       Weekday/month names always come from day-labels /
 *                       month-labels below, so use full names in those
 *                       settings (not abbreviations) if you want e.g.
 *                       "February" instead of "Feb".
 *   data-day-labels    comma-separated, Sunday-first, e.g. "Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday"
 *   data-month-labels  comma-separated, e.g. "January,February,...,December"
 *   data-days-to-skip  comma-separated lowercase weekday names, e.g. "saturday,sunday"
 *   data-dates-to-skip comma-separated ISO dates, e.g. "2026-12-25,2027-01-01"
 *
 * Expected data attributes on each [data-dynamic-date="true"] element:
 *   data-text      original text containing [start_date] and/or [end_date]
 *   data-min-days  offset used for [start_date]
 *   data-max-days  offset used for [end_date]
 */
class DynamicDates extends HTMLElement {
  connectedCallback() {
    this.dateFormat = this.dataset.dateFormat || 'MMMM D';

    this.dayLabels = this.parseList(
      this.dataset.dayLabels,
      ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    );

    this.monthLabels = this.parseList(
      this.dataset.monthLabels,
      ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    );

    // These come pre-lowercased and space-stripped from Liquid already,
    // but we normalize again here in case this element is ever used elsewhere.
    this.daysToSkip = this.parseList(this.dataset.daysToSkip, []).map(d => d.toLowerCase());
    this.datesToSkip = this.parseList(this.dataset.datesToSkip, []).map(d => d.toLowerCase());

    this.renderDates();
  }

  parseList(value, fallback) {
    if (!value) return fallback;
    const list = value.split(',').map(v => v.trim()).filter(Boolean);
    return list.length ? list : fallback;
  }

  isSkippedDate(date) {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const weekdayName = dayNames[date.getDay()];
    if (this.daysToSkip.includes(weekdayName)) return true;

    const iso = this.toISODate(date);
    if (this.datesToSkip.includes(iso)) return true;

    return false;
  }

  toISODate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Adds `days` valid (non-skipped) calendar days on top of `base`,
  // then nudges forward if the landing day itself is skipped.
  addValidDays(base, days) {
    const date = new Date(base);
    let count = 0;
    while (count < days) {
      date.setDate(date.getDate() + 1);
      if (!this.isSkippedDate(date)) count++;
    }
    while (this.isSkippedDate(date)) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  ordinalSuffix(day) {
    if (day % 100 >= 11 && day % 100 <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  formatDate(date) {
    const pad = n => String(n).padStart(2, '0');
    const day = date.getDate();
    const month = date.getMonth();
    const weekday = date.getDay();

    const weekdayName = this.dayLabels[weekday] || '';
    const monthName = this.monthLabels[month] || '';
    const dayOrdinal = `${day}${this.ordinalSuffix(day)}`;
    const dayPadded = pad(day);
    const monthPadded = pad(month + 1);

    switch (this.dateFormat) {
      case 'day_mm_dd':
        return `${weekdayName}, ${monthName} ${dayOrdinal}`;
      case 'day_dd_mm':
        return `${weekdayName}, ${day}. ${monthName}`;
      case 'mm_dd':
        return `${monthName} ${dayOrdinal}`;
      case 'dd_mm':
        return `${day}. ${monthName}`;
      case 'dd_mm_no_dot':
        return `${day} ${monthName}`;
      case 'day_dd_mm_numeric':
        return `${weekdayName}, ${dayPadded}. ${monthPadded}.`;
      case 'dd_mm_numeric':
        return `${dayPadded}. ${monthPadded}.`;
      default:
        // Unrecognized value (e.g. the setting gets a new option later) —
        // fall back to the "mm_dd" shape rather than showing nothing.
        return `${monthName} ${dayOrdinal}`;
    }
  }

  renderDates() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const items = this.querySelectorAll('[data-dynamic-date="true"]');

    items.forEach(el => {
      const text = el.dataset.text || '';
      if (!text.includes('[start_date]') && !text.includes('[end_date]')) return;

      let output = text;

      if (text.includes('[start_date]')) {
        const minDays = parseInt(el.dataset.minDays, 10) || 0;
        const startDate = this.addValidDays(today, minDays);
        output = output.replace('[start_date]', this.formatDate(startDate));
      }

      if (text.includes('[end_date]')) {
        const maxDays = parseInt(el.dataset.maxDays, 10) || 0;
        const endDate = this.addValidDays(today, maxDays);
        output = output.replace('[end_date]', this.formatDate(endDate));
      }

      el.innerHTML = output;
    });
  }
}

customElements.define('dynamic-dates', DynamicDates);